import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import styled from 'styled-components'

// Icon components
const ChevronDownIcon = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.23 8.29a.75.75 0 01.02-1.06z"
      clipRule="evenodd"
    />
  </svg>
)

const CheckIcon = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M16.704 5.296a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06 0l-3.5-3.5a.75.75 0 011.06-1.06L9 12.19l6.97-6.97a.75.75 0 011.06 0z"
      clipRule="evenodd"
    />
  </svg>
)

// Styled Components
const StyledSelectWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 20rem; /* equiv to max-w-xs */
`

const StyledSelectButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.5rem 1rem; /* px-4 py-2 */
  text-align: left;
  background-color: #fff;
  border: 1px solid #d1d5db; /* border-gray-300 */
  border-radius: 0.375rem; /* rounded-md */
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); /* shadow-sm */
  font-size: 0.875rem; /* sm:text-sm */
  line-height: 1.25rem;
  &:focus {
    outline: 2px solid transparent;
    outline-offset: 2px;
    border-color: #6366f1; /* focus:border-indigo-500 */
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.5); /* focus:ring-2 focus:ring-indigo-500 (simplified) */
  }
`

const StyledSelectedLabel = styled.span`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(props) => (props.$hasValue ? '#111827' : '#6b7280')}; /* text-gray-900 or text-gray-500 */
`

const StyledChevronIconInternal = styled(ChevronDownIcon)`
  width: 1.25rem; /* w-5 */
  height: 1.25rem; /* h-5 */
  color: #9ca3af; /* text-gray-400 */
  transform: ${(props) => (props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease-in-out;
  margin-left: 0.5rem;
`

const StyledDropdown = styled.div`
  position: absolute;
  z-index: 10;
  width: 100%;
  margin-top: 0.25rem; /* mt-1 */
  background-color: #fff;
  border: 1px solid #d1d5db; /* border-gray-300 */
  border-radius: 0.375rem; /* rounded-md */
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05); /* shadow-lg */
`

const StyledSearchWrapper = styled.div`
  padding: 0.5rem; /* p-2 */
`

const StyledSearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem; /* px-3 py-2 */
  font-size: 0.875rem; /* text-sm */
  border: 1px solid #d1d5db; /* border-gray-300 */
  border-radius: 0.375rem; /* rounded-md */
  &:focus {
    outline: 1px solid transparent; /* focus:outline-none - overriden by ring */
    box-shadow: 0 0 0 1px #6366f1; /* focus:ring-1 focus:ring-indigo-500 */
    border-color: #6366f1; /* focus:border-indigo-500 */
  }
`

const StyledNoResults = styled.p`
  padding: 0.5rem 1rem; /* px-4 py-2 */
  font-size: 0.875rem; /* text-sm */
  color: #6b7280; /* text-gray-500 */
  text-align: center;
`

const StyledOptionsList = styled.ul`
  padding-top: 0.25rem; /* py-1 */
  padding-bottom: 0.25rem; /* py-1 */
  overflow-y: auto;
  font-size: 0.875rem; /* sm:text-sm */
  line-height: 1.25rem;
  max-height: 12rem; /* Adjusted max height */
  list-style: none;
  margin: 0;
  padding-left: 0;
  &:focus {
    outline: none;
  }
  box-shadow: 0 0 0 1px rgba(0,0,0,0.05); /* ring-1 ring-black ring-opacity-5 */
`

const StyledOptionItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem; /* px-4 py-2 */
  font-size: 0.875rem; /* text-sm */
  cursor: pointer;
  color: ${(props) => (props.$isSelected ? '#fff' : '#111827')};
  background-color: ${(props) => (props.$isSelected ? '#4f46e5' : 'transparent')};
  font-weight: ${(props) => (props.$isSelected ? '600' : 'normal')};

  &:hover {
    background-color: ${(props) => (props.$isSelected ? '#4338ca' : '#eef2ff')};
    color: ${(props) => (props.$isSelected ? '#fff' : '#3730a3')};
  }
`

const StyledCheckIconInternal = styled(CheckIcon)`
  width: 1.25rem; /* w-5 */
  height: 1.25rem; /* h-5 */
  color: #fff;
`

const StyledPagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem; /* p-2 */
  border-top: 1px solid #e5e7eb; /* border-t border-gray-200 */
`

const StyledPageButton = styled.button`
  padding: 0.25rem 0.75rem; /* px-3 py-1 */
  font-size: 0.875rem; /* text-sm */
  color: #374151; /* text-gray-700 */
  background-color: #f3f4f6; /* bg-gray-100 */
  border-radius: 0.375rem; /* rounded-md */
  border: 1px solid transparent;
  &:hover:not(:disabled) {
    background-color: #e5e7eb; /* hover:bg-gray-200 */
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const StyledPageInfo = styled.span`
  font-size: 0.875rem; /* text-sm */
  color: #4b5563; /* text-gray-600 */
`

const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option...',
  itemsPerPage: itemsPerPageProp = 6,
  searchTermFn,
  onClick,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(0)
  const selectRef = useRef(null)
  const searchInputRef = useRef(null)

  const itemsPerPage = itemsPerPageProp

  const filteredOptions = useMemo(() => {
    if (!searchTerm) {
      return options
    }
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    )
  }, [options, searchTerm])

  const totalPages = useMemo(() => {
    return Math.ceil(filteredOptions.length / itemsPerPage)
  }, [filteredOptions.length, itemsPerPage])

  const currentOptions = useMemo(() => {
    const start = currentPage * itemsPerPage
    const end = start + itemsPerPage
    return filteredOptions.slice(start, end)
  }, [filteredOptions, currentPage, itemsPerPage])

  const selectedOptionDisplayLabel = useMemo(() => {
    if (!value) {
      return placeholder
    }
    const selectedOpt = options.find((opt) => opt.value === value)
    return selectedOpt ? selectedOpt.label : placeholder
  }, [value, options, placeholder])

  const handleSelectOption = useCallback(
    (optionValue) => {
      if (value === optionValue) {
        onChange(null)
      } else {
        onChange(optionValue)
      }
      setIsOpen(false)
    },
    [value, onChange],
  )

  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [])

  const handleSearchChange = useCallback(
    (event) => {
      searchTermFn(event.target.value)
      setSearchTerm(event.target.value)
      setCurrentPage(0)
    },
    [searchTermFn],
  )

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
  }, [totalPages])

  const handlePrevPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(prev - 1, 0))
  }, [])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => searchInputRef.current?.focus(), 100)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  return (
    <StyledSelectWrapper ref={selectRef} onClick={onClick}>
      <StyledSelectButton
        type="button"
        onClick={toggleOpen}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <StyledSelectedLabel $hasValue={!!value}>
          {selectedOptionDisplayLabel}
        </StyledSelectedLabel>
        <StyledChevronIconInternal $isOpen={isOpen} />
      </StyledSelectButton>

      {isOpen && (
        <StyledDropdown>
          <StyledSearchWrapper>
            <StyledSearchInput
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search..."
              aria-label="Search options"
            />
          </StyledSearchWrapper>

          {filteredOptions.length === 0 && searchTerm && (
            <StyledNoResults>No results found.</StyledNoResults>
          )}

          {filteredOptions.length > 0 && (
            <StyledOptionsList
              // role="listbox"
              aria-multiselectable="false"
            >
              {currentOptions.map((option) => {
                const isSelected = value === option.value
                return (
                  <StyledOptionItem
                    key={`${option.value}-${crypto.randomUUID()}`}
                    onClick={() => handleSelectOption(option.value)}
                    $isSelected={isSelected}
                    // role="option"
                    aria-selected={isSelected}
                  >
                    <span>{option.label}</span>
                    {isSelected && <StyledCheckIconInternal />}
                  </StyledOptionItem>
                )
              })}
            </StyledOptionsList>
          )}
          {totalPages > 1 && (
            <StyledPagination>
              <StyledPageButton
                onClick={handlePrevPage}
                disabled={currentPage === 0}
              >
                Prev
              </StyledPageButton>
              <StyledPageInfo>
                Page {currentPage + 1} of {totalPages}
              </StyledPageInfo>
              <StyledPageButton
                onClick={handleNextPage}
                disabled={currentPage === totalPages - 1}
              >
                Next
              </StyledPageButton>
            </StyledPagination>
          )}
        </StyledDropdown>
      )}
    </StyledSelectWrapper>
  )
}

export default SearchableSelect
